/**
 * Delivery Manager - Black/Gray Theme
 * Features: Zipcode validation, slot-based selection, auto-select, calendar picker, Shop Pay integration
 */

class DeliveryManager {
  constructor() {
    this.config = null;
    this.zipcodeCache = new Map();
    this.currentDayOffset = 0;
    this.selectedSlot = null;
    this.availableDates = [];
    this.flatpickrInstance = null;
    this.lastValidatedZipcode = null;
    this.checkoutButtonsEnabled = false;
    this.lastCartTotal = 0;
    this.cartPollingInterval = null;
    this.realtimeUpdateInterval = null; // For 1-minute updates
    this.customSlotsForToday = null; // Dynamic slots for today only

    this.init();
  }

  /**
   * Get current time in Miami timezone (America/New_York)
   * FIX: Bug #2 - Always use store timezone, not customer's browser timezone
   * Returns a Date object with time components adjusted to Miami timezone
   */
  getMiamiTime() {
    const now = new Date();

    // Get the time parts in Miami timezone
    const miamiFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = miamiFormatter.formatToParts(now);
    const miamiParts = {};
    parts.forEach(part => {
      if (part.type !== 'literal') {
        miamiParts[part.type] = part.value;
      }
    });

    // Create date object with Miami timezone values
    return new Date(
      miamiParts.year,
      miamiParts.month - 1, // Month is 0-indexed
      miamiParts.day,
      miamiParts.hour,
      miamiParts.minute,
      miamiParts.second
    );
  }

  /**
   * Get current date in Miami timezone (midnight)
   */
  getMiamiDate() {
    const miamiNow = this.getMiamiTime();
    miamiNow.setHours(0, 0, 0, 0);
    return miamiNow;
  }

  /**
   * Format Miami time for display (12-hour format)
   * Uses current browser time but formats it in Miami timezone
   */
  formatMiamiTime() {
    const now = new Date();
    return now.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York'
    });
  }

  /**
   * Format Miami date for display (e.g., "Oct 30")
   * Uses current browser time but formats it in Miami timezone
   */
  formatMiamiDate() {
    const now = new Date();
    return now.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'America/New_York'
    });
  }

  async init() {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🚀 DELIVERY MANAGER INITIALIZING - DEBUG MODE ENABLED');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    // Load config from JSON
    this.loadConfig();
    console.log('✅ Config loaded:', this.config);

    // Check for Google Maps API
    if (!window.google || !window.google.maps) {
      console.warn('⚠️ Google Maps API not loaded. Radius validation will be skipped.');
    } else {
      console.log('✅ Google Maps API loaded');
    }

    // Cache DOM elements
    this.cacheElements();
    console.log('✅ DOM elements cached');
    console.log('  - Checkout button:', this.checkoutButton);
    console.log('  - Cart form:', document.getElementById('cart'));
    console.log('  - Shop Pay container:', this.shopPayContainer);

    // Set up event listeners
    this.setupEventListeners();
    console.log('✅ Event listeners setup complete');

    // FIX: Bug #1 - Initialize delivery method BEFORE rendering dates
    // This ensures methodPickup/methodDelivery are set before availability checks
    this.updateDeliveryMethodState();
    console.log('✅ Delivery method state initialized');

    // NEW FLOW: Initialize date/time selection immediately
    this.generateAvailableDates();
    this.renderDateTimeColumns();
    this.autoSelectFirstAvailable();
    console.log('✅ Date/time selection initialized');

    // Start real-time updates (every 1 minute)
    this.startRealtimeUpdates();
    console.log('✅ Real-time updates started (1-minute interval)');

    // Restore zipcode from localStorage if available
    this.restoreZipcode();

    // Auto-validate zipcode if it exists on page load (e.g., after back button or page refresh)
    if (this.methodDelivery?.checked && this.zipcodeInput?.value.length === 5) {
      console.log('🔄 Re-validating existing zipcode on page load');
      this.validateZipcode();
    }

    // Update delivery fee display
    this.updateDeliveryFeeDisplay();
    console.log('✅ Delivery fee display updated');

    // Setup checkout validation
    this.setupCheckoutValidation();
    console.log('✅ Checkout validation setup complete');

    // Initialize calendar picker
    this.initCalendarPicker();
    console.log('✅ Calendar picker initialized');

    // Character counter for greeting message
    this.setupCharacterCounter();
    console.log('✅ Character counter setup complete');

    // Watch for Shop Pay buttons loading dynamically
    this.observeShopPayButtons();
    console.log('✅ Shop Pay observer active');

    // Setup sticky sign-in section
    this.setupStickySignIn();
    console.log('✅ Sticky sign-in setup complete');

    // Update min order gate on page load
    this.updateMinOrderGate();
    console.log('✅ Min order gate initialized');

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('✅ DELIVERY MANAGER READY');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  }

  loadConfig() {
    // Priority 1: Load from inline JSON config (Shopify theme customizer settings)
    const configEl = document.getElementById('delivery-config');
    if (configEl) {
      try {
        this.config = JSON.parse(configEl.textContent);
        this.normalizeConfig();
        console.log('📋 Config loaded from Shopify theme customizer (inline JSON):', this.config);
        return;
      } catch (error) {
        console.error('Failed to parse inline delivery config:', error);
      }
    }

    // Priority 2: Fallback to global config (delivery-config.js) if inline config not found
    if (window.DELIVERY_CONFIG) {
      this.config = window.DELIVERY_CONFIG;
      this.normalizeConfig();
      console.log('📋 Config loaded from delivery-config.js (fallback):', this.config);
      return;
    }

    console.error('❌ No config found! Please configure delivery settings.');
  }

  normalizeConfig() {
    // Normalize snake_case to camelCase for consistency
    if (this.config.cart_total !== undefined) {
      this.config.cartTotal = this.config.cart_total;
    }
    if (this.config.min_order_amount !== undefined) {
      this.config.minOrderAmount = this.config.min_order_amount;
    }
    if (this.config.free_delivery_threshold !== undefined) {
      this.config.freeDeliveryThreshold = this.config.free_delivery_threshold;
    }
    if (this.config.delivery_fee !== undefined) {
      this.config.deliveryFee = this.config.delivery_fee;
    }
  }

  cacheElements() {
    // Min order gate
    this.minOrderGate = document.getElementById('min-order-gate');
    this.minOrderRemaining = document.getElementById('min-order-remaining');
    this.minOrderAmount = document.getElementById('min-order-amount');
    this.minOrderProgressFill = document.getElementById('min-order-progress-fill');
    this.deliveryUIContainer = document.getElementById('delivery-ui-container');

    // Method toggle (Updated for toggle button design)
    this.methodDelivery = document.getElementById('local-delivery');
    this.methodPickup = document.getElementById('store-pickup');
    this.deliveryFeeSubtext = document.getElementById('delivery-fee-subtext');
    this.freeDeliveryText = document.getElementById('free-delivery-text');

    // Store pickup details
    this.storePickupDetails = document.getElementById('store-pickup-details');

    // Zipcode (Step 1 section)
    this.zipcodeSection = document.getElementById('zipcode-section'); // Old section (hidden)
    this.zipcodeStep1Section = document.getElementById('zipcode-section-step1'); // New Step 1 section
    this.zipcodeInput = document.getElementById('delivery-zipcode');
    this.zipcodeSuccess = document.querySelector('.delivery-zipcode-success');
    this.zipcodeExtended = document.querySelector('.delivery-zipcode-extended');
    this.zipcodeError = document.querySelector('.delivery-zipcode-error');
    this.zipcodeIncomplete = document.querySelector('.delivery-zipcode-incomplete');
    this.zipcodeIconSuccess = document.querySelector('.delivery-zipcode-icon-success');
    this.zipcodeIconError = document.querySelector('.delivery-zipcode-icon-error');

    // Inline ZIP helper text (banner removed in v2026.05.08.5)
    this.zipcodeHelperText = document.getElementById('zipcode-helper-text');

    console.log('📋 Zipcode validation elements cached:');
    console.log('   - zipcodeSuccess:', this.zipcodeSuccess);
    console.log('   - zipcodeError:', this.zipcodeError);
    console.log('   - zipcodeIncomplete:', this.zipcodeIncomplete);
    console.log('   - zipcodeIconSuccess:', this.zipcodeIconSuccess);
    console.log('   - zipcodeIconError:', this.zipcodeIconError);

    // Delivery fee
    this.deliveryFeeInfo = document.getElementById('delivery-fee-info');
    this.feeAmount = document.getElementById('fee-amount');
    this.feeHelpText = document.getElementById('fee-help-text');

    // DateTime section
    this.datetimeSection = document.getElementById('datetime-section');
    this.datetimeColumns = document.getElementById('datetime-columns');
    this.datetimeLabelDelivery = document.getElementById('datetime-label-delivery');
    this.datetimeLabelPickup = document.getElementById('datetime-label-pickup');
    this.miamiTimeDisplay = document.getElementById('miami-time-display'); // Real-time Miami time
    this.miamiDateDisplay = document.getElementById('miami-date-display'); // Real-time Miami date

    // Navigation & Calendar
    this.navPrev = document.getElementById('nav-prev');
    this.navNext = document.getElementById('nav-next');
    this.calendarButton = document.getElementById('open-calendar');
    this.calendarInput = document.getElementById('calendar-picker');

    // Hidden fields
    this.selectedDateInput = document.getElementById('selected-date');
    this.selectedTimeslotInput = document.getElementById('selected-timeslot');
    this.selectedTimestampInput = document.getElementById('selected-timestamp');

    // Free card badge
    this.freeCardBadge = document.getElementById('free-card-badge');
    this.freeCardIcon = document.getElementById('free-card-icon');
    this.freeCardText = document.getElementById('free-card-text');

    // Optional fields
    this.greetingSection = document.getElementById('greeting-section');
    this.greetingMessage = document.getElementById('greeting-message');
    this.greetingCharCount = document.getElementById('greeting-message-count');
    this.specialInstructions = document.getElementById('special-instructions');
    this.specialInstructionsCount = document.getElementById('special-instructions-count');
    this.instructionsSection = document.getElementById('instructions-section');

    // Card message section (needs validation control)
    this.cardMessageSection = document.querySelector('.delivery-message-section');

    // Order Summary elements
    this.orderSummary = document.getElementById('delivery-order-summary');
    this.summaryFullText = document.getElementById('summary-full-text');
    this.summaryCardStatus = document.getElementById('summary-card-status');
    this.summaryCardIcon = document.getElementById('summary-card-icon');
    this.summaryCardPreview = document.getElementById('summary-card-preview');
    this.summaryEditButtons = document.querySelectorAll('.delivery-summary-edit');

    // Checkout buttons
    this.checkoutButton = document.getElementById('checkout');
    this.signinButton = document.querySelector('.cart__signin-button');
    this.guestCheckoutButtons = document.querySelectorAll('.guest-checkout-action-button'); // Multiple instances
    this.shopPayContainer = document.querySelector('.cart__dynamic-checkout-buttons');

    console.log('📋 Checkout buttons cached:');
    console.log('   - checkoutButton:', this.checkoutButton);
    console.log('   - signinButton:', this.signinButton);
    console.log('   - guestCheckoutButtons count:', this.guestCheckoutButtons.length);
    console.log('   - guestCheckoutButtons:', this.guestCheckoutButtons);

    // Validation banner
    this.validationBanner = document.getElementById('delivery-validation-banner');
    this.validationBannerTitle = document.getElementById('validation-banner-title');
    this.validationBannerList = document.getElementById('validation-banner-list');
    this.validationBannerClose = document.getElementById('close-validation-banner');
  }

  setupEventListeners() {
    // Delivery method toggle - Mark as user-selected when clicked (Tasks 37-39: Updated for pills)
    const handleDeliveryClick = () => {
      document.querySelector('#local-delivery + .delivery-pill')?.classList.remove('auto-selected');
      document.querySelector('#local-delivery + .delivery-pill')?.classList.add('user-selected');
      document.querySelector('#store-pickup + .delivery-pill')?.classList.remove('auto-selected', 'user-selected');
      this.updateDeliveryMethodState();
    };
    const handlePickupClick = () => {
      document.querySelector('#store-pickup + .delivery-pill')?.classList.remove('auto-selected');
      document.querySelector('#store-pickup + .delivery-pill')?.classList.add('user-selected');
      document.querySelector('#local-delivery + .delivery-pill')?.classList.remove('auto-selected', 'user-selected');
      this.updateDeliveryMethodState();
    };

    // Use click event to catch re-clicking the already-selected option
    this.methodDelivery?.addEventListener('click', handleDeliveryClick);
    this.methodPickup?.addEventListener('click', handlePickupClick);

    // Zipcode validation - auto-validate after 5 digits + Enter key
    this.zipcodeInput?.addEventListener('input', (e) => this.handleZipcodeInput(e));
    this.zipcodeInput?.addEventListener('keydown', (e) => this.handleZipcodeKeydown(e));
    this.zipcodeInput?.addEventListener('blur', (e) => this.handleZipcodeBlur(e));

    // Character counters
    this.greetingMessage?.addEventListener('input', (e) => this.updateCharCount(e, this.greetingCharCount));
    this.specialInstructions?.addEventListener('input', (e) => this.updateCharCount(e, this.specialInstructionsCount));

    // FIX 5: auto-save card_message + special_instructions on input (debounced)
    this.greetingMessage?.addEventListener('input', () => this.scheduleAttributeSave());
    this.specialInstructions?.addEventListener('input', () => this.scheduleAttributeSave());

    // Free card badge icon/text update based on message content
    this.greetingMessage?.addEventListener('input', () => {
      this.updateFreeCardIconAndText();
      this.updateOrderSummary();
    });

    // Order Summary edit buttons
    this.summaryEditButtons?.forEach(button => {
      button.addEventListener('click', () => this.handleSummaryEdit(button.dataset.edit));
    });

    // Navigation - responsive (2 days on mobile, 3 on desktop)
    this.navPrev?.addEventListener('click', () => {
      const daysToShow = this.getDaysToShow();
      this.navigateDays(-daysToShow);
    });
    this.navNext?.addEventListener('click', () => {
      const daysToShow = this.getDaysToShow();
      this.navigateDays(daysToShow);
    });

    // Calendar button
    this.calendarButton?.addEventListener('click', () => this.openCalendar());

    // Validation banner close button
    this.validationBannerClose?.addEventListener('click', () => this.hideValidationBanner());

    // Prevent form submission on Enter in any input field
    const form = document.getElementById('delivery-options-form');
    form?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
      }
    });

    // Listen for cart updates (quantity changes) - Multiple event sources
    document.addEventListener('cart:updated', () => {
      console.log('🛒 Cart updated (custom event), refreshing delivery fee');
      this.refreshCartTotal();
    });

    // Shopify PUB_SUB_EVENTS system
    if (window.Shopify && window.Shopify.on) {
      window.Shopify.on('cart:updated', () => {
        console.log('🛒 Cart updated (Shopify event), refreshing delivery fee');
        this.refreshCartTotal();
      });
    }

    // Subscribe to PUB_SUB_EVENTS if available
    if (typeof subscribe === 'function') {
      subscribe('cartUpdate', (data) => {
        console.log('🛒 Cart updated (PUB_SUB), refreshing delivery fee', data);
        this.refreshCartTotal();
      });
    }

    // Fallback: Poll for cart changes every 2 seconds
    this.startCartPolling();

    // Re-render on window resize (mobile ↔ desktop)
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.renderDateTimeColumns();
        this.reapplySelection();
      }, 250);
    });
  }

  updateDeliveryMethodState() {
    // Ensure one method is always selected (fix browser back button issue)
    if (!this.methodDelivery?.checked && !this.methodPickup?.checked) {
      // Default to Store Pickup if neither is selected — user must opt into delivery
      // (so ZIP requirement is contextual, not surprising)
      if (this.methodPickup) {
        this.methodPickup.checked = true;
        // Mark as auto-selected visually
        document.querySelector('#store-pickup + .delivery-pill')?.classList.add('auto-selected');
        console.log('🔄 Auto-selected Store Pickup (no method was selected)');
      }
    }

    const isDelivery = this.methodDelivery?.checked;

    // Set selected method for order summary
    this.selectedMethod = isDelivery ? 'delivery' : 'pickup';

    // Silently populate hidden fields for cart attributes
    const methodInput = document.getElementById('selected-delivery-method');
    const stateInput = document.getElementById('selected-delivery-state');
    const countryInput = document.getElementById('selected-delivery-country');

    if (methodInput) {
      methodInput.value = isDelivery ? 'Local Delivery' : 'Store Pickup';
    }
    if (stateInput) {
      stateInput.value = 'FL'; // Always Florida
    }
    if (countryInput) {
      countryInput.value = 'US'; // Always United States
    }

    // FIX 5: persist method/state/country to server so any checkout path picks them up
    this.scheduleAttributeSave();

    // Show/hide ZIP section (only for delivery)
    if (this.zipcodeStep1Section) {
      this.zipcodeStep1Section.style.display = isDelivery ? 'block' : 'none';
    }

    // Show/hide store pickup details (only for pickup)
    if (this.storePickupDetails) {
      this.storePickupDetails.style.display = isDelivery ? 'none' : 'block';
    }

    // OLD: Hide old zipcode section (always hidden now)
    if (this.zipcodeSection) {
      this.zipcodeSection.style.display = 'none';
    }

    // Update delivery fee price display (show "Free" if cart >= free threshold)
    this.updateDeliveryFeeDisplay();

    // Show/hide delivery fee (hide when ZIP is entered)
    if (this.deliveryFeeInfo) {
      this.deliveryFeeInfo.style.display = (isDelivery && !this.lastValidatedZipcode) ? 'block' : 'none';
    }

    // Show/hide free delivery text (below Local Delivery card)
    if (this.freeDeliveryText) {
      this.freeDeliveryText.style.display = isDelivery ? 'block' : 'none';
    }

    // Update datetime labels (Step 2 labels)
    if (this.datetimeLabelDelivery && this.datetimeLabelPickup) {
      this.datetimeLabelDelivery.style.display = isDelivery ? 'inline' : 'none';
      this.datetimeLabelPickup.style.display = isDelivery ? 'none' : 'inline';
    }

    // NEW FLOW: Date/time is always shown and enabled (Step 2)
    // For delivery mode, also re-validate zipcode if it exists
    if (isDelivery && this.zipcodeInput?.value.length === 5) {
      this.validateZipcode();
    }

    // Update free card badge visibility
    this.updateFreeCardBadge();

    // Control card message section visibility based on validation
    this.updateCardMessageSectionVisibility();

    // v1.1.41: Start ZIP code guidance for Local Delivery
    this.startZipcodeGuidance();

    // Update order summary when method changes
    this.updateOrderSummary();

    // Re-validate checkout buttons based on new method
    // Store Pickup doesn't require ZIP, so this may enable buttons
    this.revalidateCheckout();

    // FIX: Bug #3 - Re-render date/time columns when method changes
    // This ensures pickup vs delivery availability is correctly displayed
    if (this.datetimeColumns && this.availableDates && this.availableDates.length > 0) {
      this.renderDateTimeColumns();
      this.reapplySelection();
    }

    console.log(`📍 Switched to ${isDelivery ? 'Delivery' : 'Pickup'} mode`);
  }

  // Update delivery fee price display (show "Free" if cart >= free threshold)
  updateDeliveryFeeDisplay() {
    if (!this.deliveryFeeSubtext || !this.config) return;

    const cartTotal = this.config.cartTotal || 0;
    const freeThreshold = this.config.freeDeliveryThreshold || (window.FL_CONFIG && window.FL_CONFIG.freeDeliveryThreshold) || 150;
    const deliveryFee = this.config.deliveryFee || (window.FL_CONFIG && window.FL_CONFIG.deliveryFee) || 20;

    if (cartTotal >= freeThreshold) {
      // Cart qualifies for free delivery - show "Free"
      this.deliveryFeeSubtext.textContent = 'Free';
    } else {
      // Show delivery fee amount
      const feeAmount = deliveryFee.toFixed(2);
      this.deliveryFeeSubtext.textContent = `$${feeAmount}`;
    }
  }

  saveZipcodeValue(zipcode) {
    // Save the current zipcode value (even if incomplete or invalid)
    try {
      localStorage.setItem('floralina_delivery_zipcode_value', zipcode);
      console.log('💾 Saved zipcode value to localStorage:', zipcode);
    } catch (error) {
      console.warn('Failed to save zipcode value to localStorage:', error);
    }
  }

  saveZipcodeValidation(isValid, zipcode) {
    // Save the validation state separately
    try {
      const validationData = {
        isValid: isValid,
        zipcode: zipcode,
        timestamp: Date.now()
      };
      localStorage.setItem('floralina_delivery_zipcode_validation', JSON.stringify(validationData));
      console.log('💾 Saved zipcode validation to localStorage:', validationData);
    } catch (error) {
      console.warn('Failed to save zipcode validation to localStorage:', error);
    }
  }

  clearValidationState() {
    // Clear validation state from localStorage
    try {
      localStorage.removeItem('floralina_delivery_zipcode_validation');
      console.log('🗑️ Cleared zipcode validation state');
    } catch (error) {
      console.warn('Failed to clear validation state:', error);
    }
  }

  hideAllZipcodeMessages() {
    // Hide all zipcode validation messages and icons
    if (this.zipcodeSuccess) this.zipcodeSuccess.style.display = 'none';
    if (this.zipcodeExtended) this.zipcodeExtended.style.display = 'none';
    if (this.zipcodeError) this.zipcodeError.style.display = 'none';
    if (this.zipcodeIncomplete) this.zipcodeIncomplete.style.display = 'none';
    if (this.zipcodeIconSuccess) this.zipcodeIconSuccess.style.display = 'none';
    if (this.zipcodeIconError) this.zipcodeIconError.style.display = 'none';
  }

  showZipcodeValidation(type) {
    // Unified function to show zipcode validation state
    // type: 'success', 'error', 'incomplete', or 'none'

    // First, hide all messages and icons
    this.hideAllZipcodeMessages();

    // Remove all validation classes
    if (this.zipcodeInput) {
      this.zipcodeInput.classList.remove('validated-success', 'validated-error', 'incomplete-input');
    }

    // Show appropriate message and apply styling
    switch(type) {
      case 'success':
        if (this.zipcodeSuccess) {
          this.zipcodeSuccess.style.display = 'flex';
        }
        if (this.zipcodeIconSuccess) {
          this.zipcodeIconSuccess.style.display = 'block';
        }
        if (this.zipcodeInput) {
          this.zipcodeInput.classList.add('validated-success');
        }
        console.log('✅ Showing success validation');
        break;

      case 'extended':
        if (this.zipcodeExtended) {
          this.zipcodeExtended.style.display = 'flex';
        }
        if (this.zipcodeInput) {
          this.zipcodeInput.classList.add('validated-success');
        }
        console.log('⚠️ Showing extended zone validation');
        break;

      case 'error':
        if (this.zipcodeError) {
          this.zipcodeError.style.display = 'flex';
        }
        if (this.zipcodeIconError) {
          this.zipcodeIconError.style.display = 'block';
        }
        if (this.zipcodeInput) {
          this.zipcodeInput.classList.add('validated-error');
        }
        console.log('❌ Showing error validation');
        break;

      case 'incomplete':
        if (this.zipcodeIncomplete) {
          this.zipcodeIncomplete.style.display = 'flex';
        }
        if (this.zipcodeInput) {
          this.zipcodeInput.classList.add('incomplete-input');
        }
        console.log('⚠️ Showing incomplete validation');
        break;

      case 'none':
        // All messages already hidden, classes already removed
        console.log('🔄 Cleared all validation displays');
        break;
    }
  }

  restoreZipcode() {
    try {
      const savedZipcode = localStorage.getItem('floralina_delivery_zipcode_value');
      if (savedZipcode && this.zipcodeInput && !this.zipcodeInput.value) {
        this.zipcodeInput.value = savedZipcode;
        console.log('📂 Restored zipcode value from localStorage:', savedZipcode);

        // Restore validation state if it exists and matches the current zipcode
        const validationData = localStorage.getItem('floralina_delivery_zipcode_validation');
        if (validationData) {
          try {
            const parsed = JSON.parse(validationData);
            if (parsed.zipcode === savedZipcode) {
              console.log('📂 Restoring validation state:', parsed);
              // Apply validation classes without re-running validation
              this.applyValidationState(parsed.isValid, savedZipcode);
            }
          } catch (e) {
            console.warn('Failed to parse validation data:', e);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to restore zipcode from localStorage:', error);
    }
  }

  applyValidationState(isValid, zipcode) {
    // Apply validation state without running validation logic again
    if (isValid) {
      this.showZipcodeValidation('success');
      this.lastValidatedZipcode = zipcode;
      this.updateDeliveryFee();
      const meetsMinOrder = this.config.cartTotal >= (this.config.minOrderAmount || 60);
      console.log(`🔍 Checkout validation (applyValidationState): slot=${this.selectedSlot !== null}, minOrder=${meetsMinOrder} (${this.config.cartTotal} >= ${this.config.minOrderAmount})`);
      const canCheckout = this.selectedSlot !== null && meetsMinOrder;
      this.updateCheckoutButtons(canCheckout);
      this.updateShopPayOverlay();
      // FIX 5: persist validated zip to server so any checkout path picks it up
      this.scheduleAttributeSave();
    } else {
      this.showZipcodeValidation('error');
      this.lastValidatedZipcode = null;
      this.updateCheckoutButtons(false);
      this.updateShopPayOverlay();
    }
  }

  handleZipcodeInput(e) {
    // Only allow numbers
    e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 5);

    // Save current zipcode value to localStorage (even if incomplete/invalid)
    this.saveZipcodeValue(e.target.value);

    // Clear all validation displays while typing
    this.showZipcodeValidation('none');

    // Auto-validate after 5th digit
    if (e.target.value.length === 5) {
      this.validateZipcode();
    } else if (e.target.value.length > 0 && e.target.value.length < 5) {
      // Show red border for incomplete input (1-4 digits)
      if (this.zipcodeInput) {
        this.zipcodeInput.classList.add('incomplete-input');
      }
      // Clear validation state since it's incomplete
      this.clearValidationState();
      // Clear lastValidatedZipcode to prevent bypass
      this.lastValidatedZipcode = null;
      // v1.1.43: Restart guidance (pulse + banner) for incomplete input
      this.startZipcodeGuidance();
    } else if (e.target.value.length === 0) {
      // Clear validation state when field is empty
      this.clearValidationState();
      // Clear lastValidatedZipcode to prevent bypass
      this.lastValidatedZipcode = null;
      // v1.1.43: Restart guidance (pulse + banner) for empty field
      this.startZipcodeGuidance();
    }
  }

  handleZipcodeKeydown(e) {
    // Prevent Enter key from submitting form
    if (e.key === 'Enter') {
      e.preventDefault();
      // Trigger validation if 5 digits entered
      if (e.target.value.length === 5) {
        e.target.blur(); // Trigger blur event which validates
      }
    }
  }

  handleZipcodeBlur(e) {
    const value = e.target.value.trim();

    // If incomplete (1-4 digits), show incomplete message
    if (value.length > 0 && value.length < 5) {
      this.showZipcodeValidation('incomplete');

      // Update checkout buttons state
      this.updateCheckoutButtons(false);
      this.updateShopPayOverlay();

      // Hide free card badge and card message section
      this.updateFreeCardBadge();
      this.updateCardMessageSectionVisibility();

      // Re-arm ZIP guidance — user partially edited a previously-valid zip
      this.lastValidatedZipcode = null;
      this.startZipcodeGuidance();
    } else if (value.length === 5) {
      // Validate if 5 digits (validation will show appropriate message)
      this.validateZipcode();
    } else if (value.length === 0) {
      // Clear all messages when field is empty
      this.showZipcodeValidation('none');

      // Hide free card badge and card message section when zipcode cleared
      this.updateFreeCardBadge();
      this.updateCardMessageSectionVisibility();

      // Re-arm ZIP guidance — user cleared the field after a successful validation
      this.lastValidatedZipcode = null;
      this.updateCheckoutButtons(false);
      this.updateShopPayOverlay();
      this.startZipcodeGuidance();
    }
  }

  async validateZipcode() {
    const zipcode = this.zipcodeInput?.value.trim();

    if (!zipcode || zipcode.length !== 5) {
      return;
    }

    // Check cache first
    if (this.zipcodeCache.has(zipcode)) {
      const isValid = this.zipcodeCache.get(zipcode);
      this.handleZipcodeValidation(isValid, zipcode);
      return;
    }

    // Validate through three-tier system
    const isValid = await this.validateZipcodeThreeTier(zipcode);
    this.zipcodeCache.set(zipcode, isValid);
    this.handleZipcodeValidation(isValid, zipcode);
  }

  async validateZipcodeThreeTier(zipcode) {
    const zip = parseInt(zipcode);

    console.log(`🔍 Validating ZIP: ${zipcode}`);
    console.log(`   - valid_zipcodes:`, this.config.valid_zipcodes);
    console.log(`   - blocked_zipcodes:`, this.config.blocked_zipcodes);

    // Tier 0: Check blocked_zipcodes FIRST (highest priority - always reject)
    if (this.config.blocked_zipcodes && this.config.blocked_zipcodes.length > 0) {
      for (const entry of this.config.blocked_zipcodes) {
        const trimmed = entry.trim();

        // Check if it's a range (supports both - and : syntax)
        if (trimmed.includes('-') || trimmed.includes(':')) {
          const separator = trimmed.includes('-') ? '-' : ':';
          const [start, end] = trimmed.split(separator).map(z => parseInt(z.trim()));

          if (zip >= start && zip <= end) {
            console.log(`🚫 Zipcode BLOCKED (Range: ${trimmed})`);
            return false;
          }
        } else {
          // Individual blocked ZIP code
          if (trimmed === zipcode) {
            console.log(`🚫 Zipcode BLOCKED (Exact match: ${trimmed})`);
            return false;
          }
        }
      }
    }

    // Tier 1: Check valid_zipcodes list (if configured)
    if (this.config.valid_zipcodes && this.config.valid_zipcodes.length > 0) {
      console.log(`📋 Checking against valid ZIP list (${this.config.valid_zipcodes.length} entries)`);

      for (const entry of this.config.valid_zipcodes) {
        const trimmed = entry.trim();

        // Check if it's a range (supports both - and : syntax)
        if (trimmed.includes('-') || trimmed.includes(':')) {
          const separator = trimmed.includes('-') ? '-' : ':';
          const [start, end] = trimmed.split(separator).map(z => parseInt(z.trim()));

          if (zip >= start && zip <= end) {
            console.log(`✅ Zipcode VALID (Range: ${trimmed})`);
            return true;
          }
        } else {
          // Individual ZIP code
          if (trimmed === zipcode) {
            console.log(`✅ Zipcode VALID (Exact match: ${trimmed})`);
            return true;
          }
        }
      }

      // If we have a valid_zipcodes list and ZIP not found, reject it
      console.log(`❌ Zipcode NOT in valid list`);
      return false;
    }

    // Tier 2: No valid_zipcodes list configured - fallback to Shopify API
    try {
      console.log('📡 No ZIP list configured - calling Shopify shipping rates API...');
      const response = await fetch(
        `/cart/shipping_rates.json?shipping_address[zip]=${zipcode}&shipping_address[country]=US&shipping_address[province]=FL`
      );

      if (!response.ok) {
        console.log('❌ Shopify API error:', response.status);
        return false;
      }

      const data = await response.json();
      console.log('📦 Shopify shipping rates response:', data);

      // Check if any shipping rates returned
      if (data.shipping_rates && data.shipping_rates.length > 0) {
        console.log(`✅ Zipcode validated via Shopify (${data.shipping_rates.length} rate(s) available)`);

        // Update delivery fee if available
        if (data.shipping_rates[0] && data.shipping_rates[0].price) {
          const fee = parseFloat(data.shipping_rates[0].price);
          console.log(`💰 Delivery fee from Shopify: $${fee}`);
          if (this.deliveryFeeInput) {
            this.deliveryFeeInput.value = fee;
          }
        }

        return true;
      } else {
        console.log('❌ No shipping rates available for this ZIP');
        return false;
      }
    } catch (error) {
      console.error('❌ Shopify shipping API error:', error);

      // Tier 3: Fallback to Google Distance Matrix API if available
      if (window.google && window.google.maps) {
        try {
          console.log('⚠️ Falling back to Google API...');
          const isWithinRadius = await this.validateZipcodeRadius(zipcode);
          if (isWithinRadius) {
            console.log('✅ Zipcode validated (Fallback: Google API)');
            return true;
          }
        } catch (googleError) {
          console.error('Google API validation failed:', googleError);
        }
      }

      return false;
    }
  }

  async validateZipcodeRadius(zipcode) {
    return new Promise((resolve, reject) => {
      // Get store address - handle both camelCase and snake_case
      let storeAddress = this.config.storeAddress || this.config.store_address;

      if (!storeAddress) {
        console.error('❌ Store address not configured');
        reject(new Error('Store address not configured'));
        return;
      }

      // Convert store address object to string if needed
      if (typeof storeAddress === 'object') {
        storeAddress = `${storeAddress.street}, ${storeAddress.city}, ${storeAddress.state} ${storeAddress.zip}`;
        console.log('📍 Converted store address object to string:', storeAddress);
      }

      console.log('🗺️ Calling Google Distance Matrix API:');
      console.log('   - From (origin):', storeAddress);
      console.log('   - To (destination):', zipcode);

      const service = new google.maps.DistanceMatrixService();

      service.getDistanceMatrix(
        {
          origins: [storeAddress],
          destinations: [zipcode],
          travelMode: google.maps.TravelMode.DRIVING,
          unitSystem: google.maps.UnitSystem.IMPERIAL,
        },
        (response, status) => {
          console.log('🗺️ Google API Response:');
          console.log('   - Status:', status);

          if (status === 'OK' && response.rows[0].elements[0].status === 'OK') {
            const distanceMiles = response.rows[0].elements[0].distance.value / 1609.34;
            const maxRadius = this.config.deliveryRadius || this.config.delivery_radius_miles || (window.FL_CONFIG && window.FL_CONFIG.deliveryRadius) || 30;
            console.log(`   - Distance: ${distanceMiles.toFixed(2)} miles`);
            console.log(`   - Max radius: ${maxRadius} miles`);
            console.log(`   - Within radius? ${distanceMiles <= maxRadius}`);
            resolve(distanceMiles <= maxRadius);
          } else {
            console.error('   - Error status:', status);
            if (response?.rows?.[0]?.elements?.[0]?.status) {
              console.error('   - Element status:', response.rows[0].elements[0].status);
            }
            reject(new Error('Distance Matrix API error: ' + status));
          }
        }
      );
    });
  }

  handleZipcodeValidation(isValid, zipcode) {
    console.log(`🔍 handleZipcodeValidation called - isValid: ${isValid}, zipcode: ${zipcode}`);

    if (isValid) {
      // Check if zip is in extended zone — V2.6.2
      const isExtended = this.config.extended_zipcodes && this.config.extended_zipcodes.length > 0 &&
        this.config.extended_zipcodes.some(z => z.trim() === zipcode);

      if (isExtended) {
        this.showZipcodeValidation('extended');
        console.log(`⚠️ Zipcode ${zipcode} is in extended delivery zone`);
      } else {
        this.showZipcodeValidation('success');
      }

      // Store last validated zipcode
      this.lastValidatedZipcode = zipcode;

      // Save validation state to localStorage
      this.saveZipcodeValidation(true, zipcode);

      // Show delivery fee
      this.updateDeliveryFee();

      // Check if we can enable checkout based on ALL 4 requirements
      const methodSelected = this.selectedMethod !== null;
      const slotSelected = this.selectedSlot !== null;
      const zipcodeValid = true; // We just validated it
      const meetsMinOrder = this.config.cartTotal >= (this.config.minOrderAmount || 60);
      console.log(`🔍 Checkout validation: method=${methodSelected}, slot=${slotSelected}, zip=${zipcodeValid}, minOrder=${meetsMinOrder} (${this.config.cartTotal} >= ${this.config.minOrderAmount})`);
      const canCheckout = methodSelected && slotSelected && zipcodeValid && meetsMinOrder;
      this.updateCheckoutButtons(canCheckout);
      this.updateShopPayOverlay();

      // Show free card badge when all validations pass
      this.updateFreeCardBadge();

      // Control card message section visibility based on validation
      this.updateCardMessageSectionVisibility();

      // Update order summary after zipcode validation
      this.updateOrderSummary();

      // v1.1.43: Stop pulsing and banner after successful validation
      this.startZipcodeGuidance(); // Will check isValidated and stop

      console.log(`✅ Zipcode ${zipcode} validated successfully`);

    } else {
      // Show error validation state
      this.showZipcodeValidation('error');

      // Clear last validated
      this.lastValidatedZipcode = null;

      // Save invalid validation state to localStorage
      this.saveZipcodeValidation(false, zipcode);

      // Hide delivery fee
      if (this.deliveryFeeInfo) {
        this.deliveryFeeInfo.style.display = 'none';
      }

      // Disable checkout buttons
      this.updateCheckoutButtons(false);
      this.updateShopPayOverlay();

      // Hide free card badge when validation fails
      this.updateFreeCardBadge();

      // Hide card message section when validation fails
      this.updateCardMessageSectionVisibility();

      // v1.1.43: Keep pulsing and banner on failed validation
      this.startZipcodeGuidance(); // Will check isValidated (false) and continue

      console.log(`❌ Zipcode ${zipcode} validation failed`);
    }
  }

  updateDeliveryFee() {
    if (!this.deliveryFeeInfo || !this.methodDelivery?.checked) return;

    // Hide delivery fee box when ZIP is entered
    if (this.lastValidatedZipcode) {
      this.deliveryFeeInfo.style.display = 'none';
      console.log('💵 Delivery fee box hidden (ZIP entered)');
      return;
    }

    // Handle both naming conventions
    const cartTotal = this.config.cartTotal || this.config.cart_total || 0;
    const minAmount = this.config.minOrderAmount || this.config.min_order_amount || 60;
    const fee = this.config.deliveryFee || this.config.delivery_fee || 20;
    const freeThreshold = this.config.freeDeliveryThreshold || this.config.free_delivery_threshold || 150;

    console.log('💵 updateDeliveryFee called:');
    console.log(`   - cartTotal: $${cartTotal.toFixed(2)}`);
    console.log(`   - freeThreshold: $${freeThreshold.toFixed(2)}`);
    console.log(`   - fee: $${fee.toFixed(2)}`);
    console.log(`   - minAmount: $${minAmount.toFixed(2)}`);

    // Show fee info (only when no ZIP entered yet)
    this.deliveryFeeInfo.style.display = 'block';

    // Update fee amount
    if (cartTotal >= freeThreshold) {
      this.feeAmount.textContent = 'FREE';
      this.feeAmount.classList.add('free');
    } else {
      this.feeAmount.textContent = `$${fee.toFixed(2)}`;
      this.feeAmount.classList.remove('free');
    }

    // Update help text with better messaging
    if (cartTotal >= freeThreshold) {
      // Calculate saved amount (assuming $20 delivery fee)
      const savedAmount = fee > 0 ? fee : 20;
      this.feeHelpText.textContent = `You qualify for free delivery (Saved $${savedAmount.toFixed(2)})`;
    } else if (cartTotal < minAmount) {
      const remaining = (minAmount - cartTotal).toFixed(2);
      this.feeHelpText.textContent = `Minimum order $${minAmount.toFixed(2)} for delivery. Add $${remaining} more`;
    } else {
      const remaining = (freeThreshold - cartTotal).toFixed(2);
      const percentage = Math.round((cartTotal / freeThreshold) * 100);
      this.feeHelpText.textContent = `Spend $${remaining} more for free delivery (${percentage}% there)`;
    }
  }


  setupCharacterCounter() {
    if (!this.greetingMessage || !this.charCount) return;

    this.greetingMessage.addEventListener('input', () => {
      const count = this.greetingMessage.value.length;
      this.charCount.textContent = count;
    });
  }

  async refreshCartTotal() {
    // Fetch updated cart data from Shopify
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update config with new cart total (convert cents to dollars)
      const newTotal = cart.total_price / 100;

      // Only update if total actually changed
      if (newTotal !== this.config.cartTotal) {
        this.config.cartTotal = newTotal;
        this.lastCartTotal = newTotal;

        console.log(`💰 Cart total updated: $${this.config.cartTotal.toFixed(2)}`);

        // Update min order gate (show/hide based on threshold)
        this.updateMinOrderGate();

        // Update delivery fee display (on delivery method button)
        this.updateDeliveryFeeDisplay();

        // Update delivery fee display (if zipcode validated)
        if (this.methodDelivery?.checked && this.lastValidatedZipcode) {
          this.updateDeliveryFee();
        }

        // Re-check checkout validation based on new cart total
        this.revalidateCheckout();
      }
    } catch (error) {
      console.error('Failed to refresh cart total:', error);
    }
  }

  startCartPolling() {
    // Store initial cart total
    this.lastCartTotal = this.config.cartTotal;

    // Poll every 500ms for cart changes (fast response for UX)
    this.cartPollingInterval = setInterval(async () => {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        const currentTotal = cart.total_price / 100;

        // Check if total changed
        if (currentTotal !== this.lastCartTotal) {
          const oldTotal = this.lastCartTotal ? this.lastCartTotal.toFixed(2) : '0.00';
          console.log(`🔄 Cart total changed via polling: ${oldTotal} → ${currentTotal.toFixed(2)}`);
          this.refreshCartTotal();
        }
      } catch (error) {
        console.error('Cart polling error:', error);
      }
    }, 500);

    console.log('📊 Cart polling started (every 0.5s)');
  }

  stopCartPolling() {
    if (this.cartPollingInterval) {
      clearInterval(this.cartPollingInterval);
      this.cartPollingInterval = null;
      console.log('⏹️ Cart polling stopped');
    }
  }

  revalidateCheckout() {
    // Re-check all validation requirements after cart total changes
    const methodSelected = this.selectedMethod !== null;
    const slotSelected = this.selectedSlot !== null;
    const zipcodeValid = this.selectedMethod === 'pickup' || (this.lastValidatedZipcode && this.lastValidatedZipcode.length === 5);
    const meetsMinOrder = this.config.cartTotal >= (this.config.minOrderAmount || 60);

    console.log(`🔄 Re-validating checkout: method=${methodSelected}, slot=${slotSelected}, zip=${zipcodeValid}, minOrder=${meetsMinOrder} (${this.config.cartTotal} >= ${this.config.minOrderAmount})`);

    const canCheckout = methodSelected && slotSelected && zipcodeValid && meetsMinOrder;
    this.updateCheckoutButtons(canCheckout);
    this.updateShopPayOverlay();
  }

  updateMinOrderGate() {
    // Show/hide min order progress bar based on cart total
    const minAmount = this.config.minOrderAmount || 60;
    const cartTotal = this.config.cartTotal || 0;

    console.log(`📊 Min order gate: cartTotal=${cartTotal}, minAmount=${minAmount}`);

    if (cartTotal < minAmount) {
      // Cart below minimum - show progress bar, hide delivery UI
      const remaining = minAmount - cartTotal;
      const percentage = Math.min((cartTotal / minAmount) * 100, 100);

      if (this.minOrderRemaining) {
        this.minOrderRemaining.textContent = remaining.toFixed(2);
      }
      if (this.minOrderAmount) {
        this.minOrderAmount.textContent = minAmount.toFixed(0);
      }
      if (this.minOrderProgressFill) {
        this.minOrderProgressFill.style.width = `${percentage}%`;
        console.log(`🎨 Setting progress bar width to ${percentage.toFixed(1)}% (${cartTotal}/${minAmount})`);
        console.log(`🎨 Progress fill element:`, this.minOrderProgressFill);
        console.log(`🎨 Current computed width:`, window.getComputedStyle(this.minOrderProgressFill).width);
      } else {
        console.error('❌ minOrderProgressFill element not found! Check HTML IDs.');
      }

      if (this.minOrderGate) {
        this.minOrderGate.style.display = 'block';
      }
      if (this.deliveryUIContainer) {
        this.deliveryUIContainer.style.display = 'none';
      }

      // Force disable all checkout buttons
      this.updateCheckoutButtons(false);

      // Hide ZIP banner — it lives outside delivery-ui-container so wouldn't be hidden otherwise
      this.stopZipcodeGuidance();

      console.log(`🚫 Min order gate ACTIVE: Need $${remaining.toFixed(2)} more (${percentage.toFixed(1)}% complete)`);
    } else {
      // Cart meets minimum - hide progress bar, show delivery UI
      if (this.minOrderGate) {
        this.minOrderGate.style.display = 'none';
      }
      if (this.deliveryUIContainer) {
        this.deliveryUIContainer.style.display = 'block';
      }

      // Re-validate checkout based on all requirements
      this.revalidateCheckout();

      // Re-evaluate ZIP banner now that delivery UI is visible (will show if delivery + no zip)
      this.startZipcodeGuidance();

      console.log(`✅ Min order gate PASSED: Cart total $${cartTotal.toFixed(2)} >= $${minAmount.toFixed(2)}`);
    }
  }

  observeShopPayButtons() {
    // Watch for Shop Pay buttons being added dynamically to the DOM
    if (!this.shopPayContainer) return;

    const observer = new MutationObserver(() => {
      // When Shop Pay buttons are added, apply the current enabled state
      const shopPayButtons = this.shopPayContainer.querySelectorAll('button, input[type="submit"], iframe');
      if (shopPayButtons.length > 0) {
        console.log('🔍 Shop Pay buttons detected, applying current state:', this.checkoutButtonsEnabled);
        this.updateCheckoutButtons(this.checkoutButtonsEnabled);
      }
    });

    observer.observe(this.shopPayContainer, {
      childList: true,
      subtree: true
    });
  }

  initCalendarPicker() {
    if (!this.calendarInput || typeof flatpickr === 'undefined') return;

    // Handle both naming conventions
    const maxDays = this.config.maxDaysInAdvance || this.config.days_ahead_to_show || 14;

    this.flatpickrInstance = flatpickr(this.calendarInput, {
      inline: false,
      minDate: 'today',
      maxDate: new Date().fp_incr(maxDays),
      disable: [
        (date) => {
          const dateStr = this.formatDate(date);
          return this.isDateBlocked(date, dateStr);
        }
      ],
      onChange: (selectedDates) => {
        if (selectedDates.length > 0) {
          this.handleCalendarSelection(selectedDates[0]);
        }
      }
    });
  }

  openCalendar() {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.open();
    }
  }

  handleCalendarSelection(selectedDate) {
    // Find the index of selected date in availableDates
    const selectedDateStr = this.formatDate(selectedDate);
    const dateIndex = this.availableDates.findIndex(d => this.formatDate(d) === selectedDateStr);

    if (dateIndex !== -1) {
      // Set offset to show: previous day, selected day, next day
      this.currentDayOffset = Math.max(0, dateIndex - 1);

      // Ensure we don't go beyond available dates
      if (this.currentDayOffset + 3 > this.availableDates.length) {
        this.currentDayOffset = Math.max(0, this.availableDates.length - 3);
      }

      // Re-render columns
      this.renderDateTimeColumns();

      console.log(`📅 Calendar selected: ${selectedDateStr}, showing offset ${this.currentDayOffset}`);
    }
  }

  generateAvailableDates() {
    this.availableDates = [];
    // FIX: Bug #2 - Use Miami timezone, not customer's browser timezone
    const today = this.getMiamiDate(); // Already set to midnight Miami time

    // Handle both naming conventions
    const maxDays = this.config.maxDaysInAdvance || this.config.days_ahead_to_show || 14;

    console.log('📅 Generating available dates...');
    console.log(`   - maxDays: ${maxDays}`);
    console.log(`   - config.maxDaysInAdvance: ${this.config.maxDaysInAdvance}`);
    console.log(`   - config.days_ahead_to_show: ${this.config.days_ahead_to_show}`);

    let blockedCount = 0;

    for (let i = 0; i < maxDays; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);

      const dateStr = this.formatDate(date);
      if (this.isDateBlocked(date, dateStr)) {
        blockedCount++;
        continue;
      }

      this.availableDates.push(date);
    }

    console.log(`📅 Generated ${this.availableDates.length} available dates (max: ${maxDays} days, blocked: ${blockedCount})`);
    if (this.availableDates.length === 0) {
      console.error('❌ WARNING: No available dates generated! Check your blocked dates configuration.');
    }
  }

  isDateBlocked(date, dateStr) {
    // Handle both naming conventions
    const blockedDates = this.config.blockedDates || this.config.blocked_dates || [];
    const blockedDateRanges = this.config.blockedDateRanges || this.config.blocked_date_ranges || [];

    // Check specific blocked dates
    if (blockedDates.includes(dateStr)) return true;

    // Check date ranges
    for (const range of blockedDateRanges) {
      if (dateStr >= range.start && dateStr <= range.end) return true;
    }

    // Check weekdays
    const dayOfWeek = date.getDay();
    const blockedWeekdays = this.config.blockedWeekdays || this.config.blocked_weekdays || [];
    if (blockedWeekdays.includes(dayOfWeek)) return true;

    return false;
  }

  /**
   * Check if we're on mobile (screen width < 750px)
   * @returns {boolean}
   */
  isMobile() {
    return window.innerWidth < 750;
  }

  /**
   * Get number of days to show based on screen size
   * @returns {number} 2 for mobile, 3 for desktop/tablet
   */
  getDaysToShow() {
    return this.isMobile() ? 2 : 3;
  }

  /**
   * Generate custom dynamic slots for "today" based on current Miami time
   * NEW FEATURE: Dynamic 3-hour slots starting from (current time + prep time + round up)
   */
  generateCustomSlotsForToday() {
    const miamiNow = this.getMiamiTime();
    const prepTimeHours = this.config.prepTimeHours || this.config.prep_time_hours || 3;

    const storeOpen = 9; // 9 AM
    const storeClose = 21; // 9 PM (21:00 in 24-hour format)

    // Calculate earliest delivery time: now + prep time
    const earliestTime = new Date(miamiNow.getTime() + (prepTimeHours * 60 * 60 * 1000));

    // Round up to next full hour
    let startHour = earliestTime.getHours();
    if (earliestTime.getMinutes() > 0) {
      startHour += 1;
    }

    // If before store opens, start at opening time
    if (startHour < storeOpen) {
      startHour = storeOpen;
    }

    // Check if less than 2 hours available before closing
    const hoursUntilClose = storeClose - startHour;
    if (hoursUntilClose < 2) {
      console.log(`⏰ Less than 2 hours available (${hoursUntilClose} hours). Blocking same-day delivery.`);
      return []; // Block same-day
    }

    // Generate 3-hour slots
    const slots = [];
    let currentStart = startHour;

    while (currentStart < storeClose) {
      let currentEnd = currentStart + 3;

      // Don't go past closing time
      if (currentEnd > storeClose) {
        currentEnd = storeClose;
      }

      // Calculate remaining hours after this slot
      const remainingHours = storeClose - currentEnd;

      // If next slot would be only 1 hour, merge it into current slot (make 4 hours)
      if (remainingHours === 1) {
        currentEnd = storeClose; // Extend to closing time
      }

      // Format slot label
      const formatHour = (hour) => {
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
        return `${displayHour}${period}`;
      };

      slots.push({
        start: currentStart,
        end: currentEnd,
        start_hour: currentStart,
        end_hour: currentEnd,
        label: `${formatHour(currentStart)} - ${formatHour(currentEnd)}`,
        enabled: true
      });

      // Move to next slot
      currentStart = currentEnd;

      // If we've reached closing time, stop
      if (currentStart >= storeClose) {
        break;
      }
    }

    console.log(`📅 Generated ${slots.length} custom slots for today:`, slots);
    return slots;
  }

  /**
   * Get slots for a specific day index
   * Uses configured slots for ALL days, filters out past/disabled slots for "today"
   */
  getSlotsForDay(dayIndex) {
    const configuredSlots = this.config.timeslots || [];

    // For future days, return all enabled configured slots
    if (dayIndex !== 0) {
      return configuredSlots.filter(slot => slot.enabled !== false);
    }

    // For "today", filter out:
    // 1. Disabled slots
    // 2. Slots that have already passed (current time + prep time)
    const miamiNow = this.getMiamiTime();
    const prepTimeHours = this.config.prepTimeHours || this.config.prep_time_hours || 3;

    // Calculate earliest available time: current time + prep time
    const earliestTime = new Date(miamiNow.getTime() + (prepTimeHours * 60 * 60 * 1000));
    const earliestHour = earliestTime.getHours() + (earliestTime.getMinutes() > 0 ? 1 : 0); // Round up

    return configuredSlots.filter(slot => {
      // Filter out disabled slots
      if (slot.enabled === false) {
        console.log(`⏭️ Skipping disabled slot: ${slot.label}`);
        return false;
      }

      // Filter out slots that have already passed
      const slotStart = slot.start_hour || slot.start;
      if (slotStart < earliestHour) {
        console.log(`⏭️ Skipping past slot: ${slot.label} (start: ${slotStart}, earliest: ${earliestHour})`);
        return false;
      }

      return true;
    });
  }

  renderDateTimeColumns() {
    if (!this.datetimeColumns) {
      console.error('❌ datetimeColumns container not found!');
      return;
    }

    if (!this.availableDates || this.availableDates.length === 0) {
      console.error('❌ No available dates to render!');
      console.log('   - availableDates:', this.availableDates);
      console.log('   - config.days_ahead_to_show:', this.config.days_ahead_to_show);
      console.log('   - config.maxDaysInAdvance:', this.config.maxDaysInAdvance);
      return;
    }

    const daysToShow = this.getDaysToShow();
    this.datetimeColumns.innerHTML = '';

    // Render columns until we have enough visible ones
    // Account for columns that may be hidden due to no available slots
    let visibleCount = 0;
    let renderIndex = this.currentDayOffset;
    const maxAttempts = Math.min(daysToShow + 5, this.availableDates.length); // Try up to 5 extra days

    while (visibleCount < daysToShow && renderIndex < this.availableDates.length && renderIndex < this.currentDayOffset + maxAttempts) {
      const date = this.availableDates[renderIndex];
      const column = this.createDayColumn(date, renderIndex);
      this.datetimeColumns.appendChild(column);

      // Check if this column is visible (not hidden by empty slots check)
      if (column.style.display !== 'none') {
        visibleCount++;
      }

      renderIndex++;
    }

    console.log(`📅 Rendered ${renderIndex - this.currentDayOffset} columns, ${visibleCount} visible (target: ${daysToShow})`);

    this.updateNavigationState();
    console.log('✅ Date/time columns rendered');
  }

  createDayColumn(date, dayIndex) {
    const column = document.createElement('div');
    column.className = 'delivery-day-column';
    column.dataset.date = this.formatDate(date);

    const header = document.createElement('div');
    header.className = 'delivery-day-header';

    const dayName = document.createElement('span');
    dayName.className = 'delivery-day-name';
    dayName.textContent = this.getDayLabel(dayIndex);

    const dayDate = document.createElement('span');
    dayDate.className = 'delivery-day-date';
    dayDate.textContent = this.formatDateDisplay(date);

    header.appendChild(dayName);
    header.appendChild(dayDate);

    const timeslotsContainer = document.createElement('div');
    timeslotsContainer.className = 'delivery-timeslots';

    // Get slots for this day (custom for today, standard for future days)
    const slotsForDay = this.getSlotsForDay(dayIndex);

    let hasAvailableSlots = false;
    slotsForDay.forEach(slot => {
      const button = this.createTimeslotButton(date, dayIndex, slot);
      timeslotsContainer.appendChild(button);
      // Check if at least one slot is available (not disabled)
      if (!button.disabled) {
        hasAvailableSlots = true;
      }
    });

    // If no slots are available, hide the entire column
    if (!hasAvailableSlots) {
      column.style.display = 'none';
      console.log(`⏭️ Hiding column for ${this.formatDate(date)} - all timeslots unavailable`);
    }

    column.appendChild(header);
    column.appendChild(timeslotsContainer);

    return column;
  }

  createTimeslotButton(date, dayIndex, slot) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'delivery-timeslot-button';
    button.textContent = slot.label;
    button.dataset.date = this.formatDate(date);
    button.dataset.timeslot = slot.label;
    button.dataset.start = slot.start;

    const isAvailable = this.isTimeslotAvailable(date, dayIndex, slot);
    button.disabled = !isAvailable;

    if (isAvailable) {
      button.addEventListener('click', () => this.selectTimeslot(button, 'user'));
    }

    return button;
  }

  isTimeslotAvailable(date, dayIndex, slot) {
    // FIX: Slots now use Miami timezone and respect configured timeslots
    // For today, slots are already filtered in getSlotsForDay() based on:
    // 1. Enabled/disabled status
    // 2. Current Miami time + prep time
    // So all returned slots are available by definition
    const isToday = dayIndex === 0;

    if (isToday) {
      // Today's slots are pre-filtered by getSlotsForDay()
      // If the slot exists, it's available
      console.log(`✅ Slot ${slot.label} available (pre-validated for today)`);
      return true;
    }

    // For future days, all enabled slots are available
    return true;
  }

  selectTimeslot(button, selectionType) {
    document.querySelectorAll('.delivery-timeslot-button').forEach(btn => {
      btn.classList.remove('selected-by-user', 'selected-auto');
    });

    if (selectionType === 'user') {
      button.classList.add('selected-by-user');
    } else {
      button.classList.add('selected-auto');
    }

    this.selectedSlot = {
      date: button.dataset.date,
      timeslot: button.dataset.timeslot,
      type: selectionType,
      timestamp: Date.now()
    };

    this.selectedDateInput.value = button.dataset.date;
    this.selectedTimeslotInput.value = button.dataset.timeslot;
    this.selectedTimestampInput.value = this.selectedSlot.timestamp;

    // FIX 5: persist to server immediately so attributes survive any checkout path
    this.scheduleAttributeSave();

    if (this.greetingSection) this.greetingSection.style.display = 'block';
    if (this.instructionsSection) this.instructionsSection.style.display = 'block';

    // Check if we can enable checkout based on ALL 4 requirements
    const methodSelected = this.selectedMethod !== null;
    const slotSelected = this.selectedSlot !== null;
    const zipcodeValid = this.selectedMethod === 'pickup' || (this.lastValidatedZipcode && this.lastValidatedZipcode.length === 5);
    const meetsMinOrder = this.config.cartTotal >= (this.config.minOrderAmount || 60);
    console.log(`🔍 Checkout validation (selectSlot): method=${methodSelected}, slot=${slotSelected}, zip=${zipcodeValid}, minOrder=${meetsMinOrder} (${this.config.cartTotal} >= ${this.config.minOrderAmount})`);
    const canCheckout = methodSelected && slotSelected && zipcodeValid && meetsMinOrder;
    this.updateCheckoutButtons(canCheckout);
    this.updateShopPayOverlay();

    // Show free card badge when all validations pass
    this.updateFreeCardBadge();

    // Control card message section visibility based on validation
    this.updateCardMessageSectionVisibility();

    // Update order summary after slot selection
    this.updateOrderSummary();

    console.log('✅ Slot selected:', this.selectedSlot);
  }

  autoSelectFirstAvailable() {
    const firstAvailableButton = document.querySelector('.delivery-timeslot-button:not(:disabled)');

    if (firstAvailableButton) {
      this.selectTimeslot(firstAvailableButton, 'auto');
      console.log('🤖 Auto-selected first available slot');
    }
  }

  navigateDays(offset) {
    this.currentDayOffset += offset;

    if (this.currentDayOffset < 0) {
      this.currentDayOffset = 0;
    }

    const daysToShow = this.getDaysToShow();
    if (this.currentDayOffset + daysToShow > this.availableDates.length) {
      this.currentDayOffset = Math.max(0, this.availableDates.length - daysToShow);
    }

    this.renderDateTimeColumns();
    this.reapplySelection();
  }

  reapplySelection() {
    if (!this.selectedSlot) return;

    const button = document.querySelector(
      `.delivery-timeslot-button[data-date="${this.selectedSlot.date}"][data-timeslot="${this.selectedSlot.timeslot}"]`
    );

    if (button && !button.disabled) {
      this.selectTimeslot(button, this.selectedSlot.type);
    }
  }

  updateNavigationState() {
    if (this.navPrev) {
      this.navPrev.disabled = this.currentDayOffset === 0;
    }

    if (this.navNext) {
      const daysToShow = this.getDaysToShow();
      const maxOffset = Math.max(0, this.availableDates.length - daysToShow);
      this.navNext.disabled = this.currentDayOffset >= maxOffset;
    }
  }

  getDayLabel(dayIndex) {
    const date = this.availableDates[dayIndex];
    if (!date) return '';

    // Label by the real calendar gap from Miami "today" — NOT the array
    // position. Blocked dates are filtered out of availableDates, so the
    // first available day may not be today (e.g. when today/tomorrow are
    // blocked). Using the date difference keeps "Today"/"Tomorrow" accurate.
    const today = this.getMiamiDate(); // midnight Miami today
    const columnDate = new Date(date);
    columnDate.setHours(0, 0, 0, 0);

    const dayDiff = Math.round((columnDate - today) / 86400000);

    if (dayDiff === 0) return 'Today';
    if (dayDiff === 1) return 'Tomorrow';
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }

  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDateDisplay(date) {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  }

  disableFields() {
    if (this.datetimeSection) {
      this.datetimeSection.style.display = 'none';
    }
    if (this.greetingSection) {
      this.greetingSection.style.display = 'none';
    }
    if (this.instructionsSection) {
      this.instructionsSection.style.display = 'none';
    }
    this.updateCheckoutButtons(false);
  }

  enableFields() {
    if (this.datetimeSection) {
      this.datetimeSection.style.display = 'block';
    }
  }

  updateCharCount(e, counterElement) {
    const count = e.target.value.length;
    if (counterElement) {
      counterElement.textContent = count;
    }
  }

  updateCheckoutButtons(enabled) {
    console.log(`🔘 updateCheckoutButtons called with enabled: ${enabled}`);

    // Query ALL checkout buttons directly (no caching) - catches all variants
    const allCheckoutButtons = document.querySelectorAll(
      '#checkout, #logged-out-checkout, .cart__checkout-button, button[name="checkout"]'
    );

    console.log(`   - Found ${allCheckoutButtons.length} checkout buttons`);
    allCheckoutButtons.forEach((button, index) => {
      button.disabled = !enabled;
      if (!enabled) {
        button.classList.add('disabled');
        button.style.opacity = '0.4';
        button.style.pointerEvents = 'none';
        button.style.cursor = 'not-allowed';
      } else {
        button.classList.remove('disabled');
        button.style.opacity = '';
        button.style.pointerEvents = '';
        button.style.cursor = '';
      }
      console.log(`   - Checkout button ${index + 1} (${button.id || button.className}) disabled: ${!enabled}`);
    });

    // Floralina button
    const floralinaBtn = document.getElementById('floralina-login-btn');
    if (floralinaBtn) {
      floralinaBtn.disabled = !enabled;
      if (!enabled) {
        floralinaBtn.classList.add('disabled');
        floralinaBtn.style.opacity = '0.4';
        floralinaBtn.style.pointerEvents = 'none';
        floralinaBtn.style.cursor = 'not-allowed';
      } else {
        floralinaBtn.classList.remove('disabled');
        floralinaBtn.style.opacity = '';
        floralinaBtn.style.pointerEvents = '';
        floralinaBtn.style.cursor = '';
      }
      console.log(`   - Floralina button disabled: ${!enabled}`);
    }

    // Express checkout containers + Shopify accelerated checkout web components.
    // FIX (2026-05-08): Use display:none (not pointer-events:none) — iframe-based widgets
    // (Shop Pay, PayPal, Amazon Pay) ignore pointer-events. Hide the entire container so
    // they cannot be clicked. Broaden selector to include <shopify-accelerated-checkout-cart>
    // (used for signed-in Shop customers) and <shopify-payment-button> (PayPal/Amazon Pay).
    const expressContainers = document.querySelectorAll(
      '.cart__dynamic-checkout-buttons, .additional-checkout-buttons, ' +
      'shopify-accelerated-checkout-cart, shopify-payment-button'
    );

    console.log(`   - Found ${expressContainers.length} express checkout containers/widgets`);

    expressContainers.forEach((container) => {
      if (!enabled) {
        container.style.display = 'none';
      } else {
        container.style.display = '';
      }
    });

    // Store the current enabled state for future reference
    this.checkoutButtonsEnabled = enabled;

    console.log(`✅ updateCheckoutButtons complete - all buttons ${enabled ? 'enabled' : 'disabled'}`);
  }

  updateFreeCardBadge() {
    // Show free card badge only when all validations pass:
    // 1. Delivery method selected
    // 2. Time slot selected
    // 3. Zipcode validated (for local delivery) OR pickup method selected

    if (!this.freeCardBadge) return;

    const methodSelected = this.selectedMethod !== null;
    const slotSelected = this.selectedSlot !== null;
    const zipcodeValid = this.selectedMethod === 'pickup' || (this.lastValidatedZipcode && this.lastValidatedZipcode.length === 5);

    const allValidationsPassed = methodSelected && slotSelected && zipcodeValid;

    if (allValidationsPassed) {
      this.freeCardBadge.style.display = 'flex';
      // Update icon and text based on message content
      this.updateFreeCardIconAndText();
      // Update order summary
      this.updateOrderSummary();
      console.log('✅ Free card badge shown - all validations passed');
    } else {
      this.freeCardBadge.style.display = 'none';
      // Update order summary (will hide it if incomplete)
      this.updateOrderSummary();
      console.log('❌ Free card badge hidden - validations incomplete');
    }
  }

  updateFreeCardIconAndText() {
    // Update icon and text based on whether card message has content
    if (!this.freeCardIcon || !this.freeCardText || !this.greetingMessage) return;

    const hasMessage = this.greetingMessage.value.trim().length > 0;

    if (hasMessage) {
      // Show checkmark + "Free custom card selected"
      this.freeCardIcon.innerHTML = `
        <circle cx="10" cy="10" r="9" fill="#34C759"/>
        <path d="M6 10L9 13L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      `;
      this.freeCardText.innerHTML = '💌 Free custom card selected <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style="display: inline-block; vertical-align: middle; margin-left: 4px;"><circle cx="8" cy="8" r="7" fill="#34C759"/><path d="M5 8L7 10L11 6" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    } else {
      // Show "Free custom card available"
      this.freeCardIcon.innerHTML = `
        <circle cx="10" cy="10" r="9" fill="#34C759"/>
        <path d="M8 6l4 4-4 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      `;
      this.freeCardText.textContent = '💌 Free custom card available';
    }
  }

  updateCardMessageSectionVisibility() {
    // v1.1.43: Always show Step 3 card message section (same as Store Pickup behavior)
    // Consistent UI: Both delivery methods show Step 3 immediately
    // No ZIP validation requirement for visibility

    if (!this.cardMessageSection) return;

    const methodSelected = this.selectedMethod !== null;
    const slotSelected = this.selectedSlot !== null;

    // Show if method and slot selected (no ZIP requirement)
    if (methodSelected && slotSelected) {
      this.cardMessageSection.style.display = 'block';
      console.log('✅ Card message form shown - method + slot selected');
    } else {
      this.cardMessageSection.style.display = 'none';
      console.log('❌ Card message form hidden - method/slot not selected');
    }
  }

  // v1.1.43: ZIP Code Guidance - Always pulse/show banner unless validation successful
  startZipcodeGuidance() {
    // Show guidance for Local Delivery when ZIP is NOT successfully validated
    const isDelivery = this.methodDelivery?.checked;
    const isValidated = this.lastValidatedZipcode && this.lastValidatedZipcode.length === 5;
    const meetsMinOrder = (this.config.cartTotal || 0) >= (this.config.minOrderAmount || 60);

    // Suppress banner when min-order isn't met — the min-order gate is what blocks checkout,
    // and the banner sits outside delivery-ui-container so it isn't auto-hidden by the gate.
    if (!isDelivery || isValidated || !meetsMinOrder) {
      this.stopZipcodeGuidance();
      return;
    }

    console.log('🎯 ZIP guidance active: pulsing + helper text emphasized');

    // Add continuous pulsing animation (even during failed validation or incomplete input)
    if (this.zipcodeInput) {
      this.zipcodeInput.classList.add('pulse-required');
    }

    // Emphasize the inline helper text (red + bold)
    if (this.zipcodeHelperText) {
      this.zipcodeHelperText.classList.add('guidance-active');
    }
  }

  stopZipcodeGuidance() {
    // De-emphasize helper text
    if (this.zipcodeHelperText) {
      this.zipcodeHelperText.classList.remove('guidance-active');
    }

    // Remove pulsing class
    if (this.zipcodeInput) {
      this.zipcodeInput.classList.remove('pulse-required');
    }

    console.log('✅ ZIP code guidance stopped (validation successful or n/a)');
  }

  /**
   * Update Miami time display in real-time
   * NEW FEATURE: Show current Miami time to customers
   */
  updateMiamiTimeDisplay() {
    // Update time display
    if (this.miamiTimeDisplay) {
      const timeString = this.formatMiamiTime();
      this.miamiTimeDisplay.textContent = timeString;
    }

    // Update date display
    if (this.miamiDateDisplay) {
      const dateString = this.formatMiamiDate();
      this.miamiDateDisplay.textContent = dateString;
    }
  }

  /**
   * Start real-time updates (every 1 minute)
   * Updates: Miami time display and slot availability
   */
  startRealtimeUpdates() {
    // Initial update
    this.updateMiamiTimeDisplay();

    // Clear any existing interval
    if (this.realtimeUpdateInterval) {
      clearInterval(this.realtimeUpdateInterval);
    }

    // Update every 1 minute (60000ms)
    this.realtimeUpdateInterval = setInterval(() => {
      console.log('⏱️ Real-time update triggered (1-minute interval)');

      // Update Miami time display
      this.updateMiamiTimeDisplay();

      // Re-generate dates and re-render slots (in case day changed or slots expired)
      this.generateAvailableDates();
      this.renderDateTimeColumns();
      this.reapplySelection();
    }, 60000); // 60 seconds

    console.log('✅ Real-time updates started (interval: 1 minute)');
  }

  /**
   * Stop real-time updates (cleanup)
   */
  stopRealtimeUpdates() {
    if (this.realtimeUpdateInterval) {
      clearInterval(this.realtimeUpdateInterval);
      this.realtimeUpdateInterval = null;
      console.log('⏹️ Real-time updates stopped');
    }
  }

  updateOrderSummary() {
    // Update the order summary with current selections
    if (!this.orderSummary) return;

    // Check if all required selections are made
    const methodSelected = this.selectedMethod !== null;
    const slotSelected = this.selectedSlot !== null;
    const zipcodeValid = this.selectedMethod === 'pickup' || (this.lastValidatedZipcode && this.lastValidatedZipcode.length === 5);

    const allComplete = methodSelected && slotSelected && zipcodeValid;

    if (allComplete) {
      // Show summary
      this.orderSummary.style.display = 'block';

      // Get elements
      const methodIcon = document.getElementById('summary-method-icon');
      const summaryFullText = document.getElementById('summary-full-text');

      if (!summaryFullText || !methodIcon) return;

      // Get date & time info
      let dateText = '';
      let timeText = '';
      if (this.selectedSlot) {
        // Parse date in local timezone (avoid UTC conversion issue)
        const [year, month, day] = this.selectedSlot.date.split('-').map(Number);
        const dateObj = new Date(year, month - 1, day); // month is 0-indexed
        const options = { month: 'short', day: 'numeric', year: 'numeric' };
        dateText = dateObj.toLocaleDateString('en-US', options);
        timeText = this.selectedSlot.timeslot;
      }

      // Get store address from config
      const storeAddress = this.config.store_address || '123 Main St, Miami, FL 33100';
      const storeAddressEncoded = encodeURIComponent(storeAddress);
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${storeAddressEncoded}`;

      if (this.selectedMethod === 'delivery') {
        // Local Delivery format
        // "Local delivery to [zipcode] on [date] between [time]"
        methodIcon.innerHTML = `
          <path d="M1 3H4.61L5.4 6M5.4 6L7.6 15H19.2L22 6H5.4ZM9 19C9 19.5523 8.55228 20 8 20C7.44772 20 7 19.5523 7 19C7 18.4477 7.44772 18 8 18C8.55228 18 9 18.4477 9 19ZM20 19C20 19.5523 19.5523 20 19 20C18.4477 20 18 19.5523 18 19C18 18.4477 18.4477 18 19 18C19.5523 18 20 18.4477 20 19Z" stroke-linecap="round" stroke-linejoin="round"/>
        `;

        summaryFullText.innerHTML = `
          <span class="summary-method-black">Local delivery</span>
          <span class="summary-connector-grey"> to </span>
          <span class="summary-value-blue">${this.lastValidatedZipcode}</span>
          <span class="summary-connector-grey"> on </span>
          <span class="summary-value-blue">${dateText}</span>
          <span class="summary-connector-grey"> between </span>
          <span class="summary-value-blue">${timeText}</span>
        `;
      } else if (this.selectedMethod === 'pickup') {
        // Store Pickup format
        // "Store pickup at [address] on [date] between [time]"
        methodIcon.innerHTML = `
          <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9 22V12H15V22" stroke-linecap="round" stroke-linejoin="round"/>
        `;

        summaryFullText.innerHTML = `
          <span class="summary-method-black">Store pickup</span>
          <span class="summary-connector-grey"> at </span>
          <a href="${googleMapsUrl}" target="_blank" class="summary-address-link">${storeAddress}</a>
          <span class="summary-connector-grey"> on </span>
          <span class="summary-value-blue">${dateText}</span>
          <span class="summary-connector-grey"> between </span>
          <span class="summary-value-blue">${timeText}</span>
        `;
      }

      // Update card status
      this.updateSummaryCardStatus();

      console.log('✅ Order summary updated and shown');
    } else {
      // Hide summary if selections incomplete
      this.orderSummary.style.display = 'none';
      console.log('ℹ️ Order summary hidden - selections incomplete');
    }
  }

  updateSummaryCardStatus() {
    // Update card message status in summary
    if (!this.greetingMessage || !this.summaryCardStatus || !this.summaryCardIcon || !this.summaryCardPreview) return;

    const hasMessage = this.greetingMessage.value.trim().length > 0;

    if (hasMessage) {
      // Show checkmark + "Custom card added"
      this.summaryCardIcon.innerHTML = `
        <path d="M13 4L6 11L3 8" stroke="#34C759" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      `;
      this.summaryCardStatus.textContent = 'Custom card added';

      // Show first 50 characters of message in grey with "Message" in black
      const preview = this.greetingMessage.value.substring(0, 50);
      const displayPreview = preview.length < this.greetingMessage.value.length ? preview + '...' : preview;
      this.summaryCardPreview.innerHTML = `<span style="color: #1D1D1F; font-weight: 500;">Message:</span> ${displayPreview}`;
      this.summaryCardPreview.style.display = 'block';
    } else {
      // Show X + "Message card not chosen"
      this.summaryCardIcon.innerHTML = `
        <path d="M12 4L4 12M4 4l8 8" stroke="#86868B" stroke-width="2" stroke-linecap="round"/>
      `;
      this.summaryCardStatus.textContent = 'Message card not chosen';
      this.summaryCardPreview.style.display = 'none';
    }
  }

  handleSummaryEdit(section) {
    // Handle edit button clicks - scroll to section and highlight
    let targetElement = null;

    switch(section) {
      case 'method':
        targetElement = document.getElementById('delivery-method-section');
        break;
      case 'datetime':
        targetElement = document.getElementById('datetime-section');
        break;
      case 'zipcode':
        targetElement = this.zipcodeInput;
        break;
      case 'card':
        targetElement = document.getElementById('deliveryMessageToggle');
        // Expand the message section
        const messageContent = document.getElementById('deliveryMessageContent');
        if (messageContent && messageContent.style.display === 'none') {
          document.getElementById('deliveryMessageToggle').click();
        }
        // Focus on greeting message field
        setTimeout(() => {
          if (this.greetingMessage) {
            this.greetingMessage.focus();
          }
        }, 300);
        break;
    }

    if (targetElement) {
      // Scroll to element with smooth behavior
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Add highlight effect
      targetElement.classList.add('delivery-section-highlight');
      setTimeout(() => {
        targetElement.classList.remove('delivery-section-highlight');
      }, 2000);

      console.log(`📝 Scrolled to ${section} section`);
    }
  }

  setupCheckoutValidation() {
    // Validate on checkout button click
    if (this.checkoutButton) {
      this.checkoutButton.addEventListener('click', async (e) => {
        console.log('🔘 Checkout button clicked');

        // ALWAYS prevent default - we'll handle everything
        e.preventDefault();
        e.stopPropagation();
        console.log('⛔ Default form submission prevented');

        // Disable button to prevent double-clicks
        this.checkoutButton.disabled = true;
        this.checkoutButton.textContent = 'Processing...';

        if (!this.validateCheckout()) {
          console.log('❌ Validation failed');
          const errors = this.getValidationErrors();
          this.showValidationBanner('Please complete your delivery details', errors);
          this.checkoutButton.disabled = false;
          this.checkoutButton.textContent = 'Checkout';
          return false;
        }
        console.log('✅ Validation passed');

        // Check session staleness
        if (!this.checkSessionStaleness()) {
          console.log('⚠️ Session staleness check failed');
          this.checkoutButton.disabled = false;
          this.checkoutButton.textContent = 'Checkout';
          return false;
        }
        console.log('✅ Session staleness check passed');

        console.log('🚀 NEW APPROACH: Redirecting directly to checkout...');
        console.log('   Note: Cart attributes will be sent via URL parameters');

        // IMPORTANT: Update cart note with delivery details BEFORE checkout
        console.log('📝 Updating cart note with delivery details...');
        await this.updateCartNoteWithDeliveryInfo();
        console.log('✅ Cart note updated successfully');

        try {
          // Build checkout URL with attributes as query parameters.
          // FIX (2026-05-08): Read hidden inputs by DOM query, NOT via FormData(form).
          // The hidden inputs with name="attributes[*]" are rendered by cart-delivery-options.liquid
          // OUTSIDE of <form id="cart"> (which only wraps line items). Using FormData on the cart
          // form returned an empty attribute set → no cart attributes saved on the order.
          const params = new URLSearchParams();
          document.querySelectorAll('input[name^="attributes["]').forEach((input) => {
            if (input.value) {
              console.log(`  📝 ${input.name}: ${input.value}`);
              params.append(input.name, input.value);
            }
          });

          // Smart pre-fill based on delivery method (fixes pickup geolocation issue)
          const isLoggedIn = document.body.classList.contains('customer-logged-in');

          if (this.selectedMethod === 'pickup') {
            // PICKUP: Pre-fill store ZIP (33169) for ALL users to ensure pickup location shows
            // This fixes the geolocation issue where distant/international customers don't see pickup option
            params.append('checkout[shipping_address][zip]', (window.FL_CONFIG && window.FL_CONFIG.storeZip) || '33169');
            params.append('checkout[shipping_address][city]', 'Miami');
            params.append('checkout[shipping_address][province]', 'FL');
            params.append('checkout[shipping_address][country]', 'US');
            console.log('📦 Store Pickup - pre-filling store ZIP: 33169 (fixes geolocation for all customers)');
          } else if (!isLoggedIn && this.selectedMethod === 'delivery' && this.lastValidatedZipcode) {
            // DELIVERY: Guest user - pre-fill validated ZIP, state, country
            params.append('checkout[shipping_address][zip]', this.lastValidatedZipcode);
            params.append('checkout[shipping_address][province]', 'FL');
            params.append('checkout[shipping_address][country]', 'US');
            console.log('👤 Guest user delivery - pre-filling ZIP:', this.lastValidatedZipcode);
          } else if (isLoggedIn) {
            console.log('🔐 Logged-in user - using saved address');
          }

          // Build the checkout URL
          let checkoutUrl = '/checkout';
          const paramString = params.toString();
          if (paramString) {
            checkoutUrl = `/checkout?${paramString}`;
          }

          console.log('🔄 Redirecting to:', checkoutUrl);
          console.log('📦 Attributes will be added to checkout');

          // Redirect to checkout
          window.location.href = checkoutUrl;

        } catch (error) {
          console.error('❌ Exception during redirect:', error);
          console.error('Error stack:', error.stack);
          alert('An error occurred. Please check the console and try again.');
          this.checkoutButton.disabled = false;
          this.checkoutButton.textContent = 'Checkout';
        }
      });
    } else {
      console.warn('⚠️ Checkout button not found during initialization');
    }

    // COMPREHENSIVE Shop Pay Blocking System
    this.setupShopPayBlocking();
  }

  setupShopPayBlocking() {
    if (!this.shopPayContainer) {
      console.warn('⚠️ Shop Pay container not found');
      return;
    }

    // Method 1: Intercept form submissions (capture phase)
    this.shopPayContainer.addEventListener('submit', (e) => {
      if (!this.validateCheckout()) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const errors = this.getValidationErrors();
        this.showValidationBanner('Please complete your delivery details', errors);
        return false;
      }
    }, true);

    // Method 2: Intercept all clicks (capture phase)
    this.shopPayContainer.addEventListener('click', (e) => {
      if (!this.validateCheckout()) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const errors = this.getValidationErrors();
        this.showValidationBanner('Please complete your delivery details', errors);
        return false;
      }
    }, true);

    // Method 3: Intercept pointer events (capture phase)
    this.shopPayContainer.addEventListener('pointerdown', (e) => {
      if (!this.validateCheckout()) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    }, true);

    // Method 4: Block mousedown events (capture phase)
    this.shopPayContainer.addEventListener('mousedown', (e) => {
      if (!this.validateCheckout()) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    }, true);

    // Method 5: Block touchstart events for mobile (capture phase)
    this.shopPayContainer.addEventListener('touchstart', (e) => {
      if (!this.validateCheckout()) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const errors = this.getValidationErrors();
        this.showValidationBanner('Please complete your delivery details', errors);
        return false;
      }
    }, true);

    // Method 6: Add a blocking overlay when validation fails
    this.createShopPayOverlay();

    console.log('🛡️ Shop Pay blocking system activated (6 layers)');
  }

  createShopPayOverlay() {
    // Create invisible overlay that blocks Shop Pay when validation fails
    const overlay = document.createElement('div');
    overlay.id = 'shop-pay-validator-overlay';
    overlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: transparent;
      z-index: 1000;
      cursor: not-allowed;
      display: none;
    `;

    overlay.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const errors = this.getValidationErrors();
      this.showValidationBanner('Please complete your delivery details', errors);
    });

    this.shopPayContainer.style.position = 'relative';
    this.shopPayContainer.appendChild(overlay);
    this.shopPayOverlay = overlay;

    console.log('🛡️ Shop Pay overlay created');
  }

  updateShopPayOverlay() {
    if (!this.shopPayOverlay) return;

    const isValid = this.validateCheckout();
    this.shopPayOverlay.style.display = isValid ? 'none' : 'block';

    console.log(`🛡️ Shop Pay overlay: ${isValid ? 'hidden' : 'visible'}`);
  }

  validateCheckout() {
    // Check minimum order for BOTH delivery and pickup
    const cartTotal = this.config.cartTotal;
    const minAmount = this.config.minOrderAmount;

    if (cartTotal < minAmount) {
      console.log('❌ Cart total below minimum:', cartTotal, '<', minAmount);
      return false;
    }

    // Check zipcode validated (LOCAL DELIVERY ONLY)
    if (this.selectedMethod === 'delivery') {
      const zipcode = this.zipcodeInput?.value.trim();
      if (!zipcode || zipcode.length !== 5 || !this.lastValidatedZipcode) {
        console.log('❌ Zipcode not validated for local delivery');
        return false;
      }
    }

    // Check date/time selected
    if (!this.selectedSlot) {
      console.log('❌ No slot selected');
      return false;
    }

    console.log('✅ Checkout validation passed');
    return true;
  }

  checkSessionStaleness() {
    if (!this.selectedSlot) return true;

    const hoursSinceSelection = (Date.now() - this.selectedSlot.timestamp) / (1000 * 60 * 60);

    if (hoursSinceSelection > 2) {
      console.warn('⚠️ Session is stale (>2 hours). Re-validating slot availability...');

      const button = document.querySelector(
        `.delivery-timeslot-button[data-date="${this.selectedSlot.date}"][data-timeslot="${this.selectedSlot.timeslot}"]`
      );

      if (!button || button.disabled) {
        this.showValidationBanner('Time Slot Unavailable', ['Your selected time slot is no longer available. Please select another slot.']);
        this.autoSelectFirstAvailable();
        return false;
      }

      this.selectedSlot.timestamp = Date.now();
      this.selectedTimestampInput.value = this.selectedSlot.timestamp;
    }

    return true;
  }

  /**
   * Update cart note with delivery information so it's visible in checkout
   * Creates 4 sections: Delivery Summary, Card Message, Special Instructions, Delivery Instructions
   * Shows "Not provided by [Customer Name]" for empty fields
   */
  // Lightweight save: persist cart attributes to the server WITHOUT rebuilding the note.
  // FIX 5 (2026-05-08): Auto-save on field change so cart attributes are always current,
  // regardless of which checkout path the customer takes (regular Checkout, Shop Pay, PayPal, etc.).
  // Debounced via scheduleAttributeSave().
  async saveCartAttributes() {
    try {
      const attrPayload = {};
      document.querySelectorAll('input[name^="attributes["]').forEach((input) => {
        const m = input.name.match(/^attributes\[([^\]]+)\]$/);
        if (m && input.value !== '') {
          attrPayload[m[1]] = input.value;
        }
      });
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrPayload })
      });
      console.log('🏷  Auto-saved cart attributes:', attrPayload);
    } catch (err) {
      console.error('Auto-save cart attributes failed (non-fatal):', err);
    }
  }

  scheduleAttributeSave() {
    clearTimeout(this._attrSaveTimer);
    this._attrSaveTimer = setTimeout(() => this.saveCartAttributes(), 500);
  }

  async updateCartNoteWithDeliveryInfo() {
    try {
      console.log('📝 Updating cart note with all customer information');

      // Get current cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Get customer name for "Not provided" messages
      let customerName = 'Customer';
      try {
        // Try to get customer email from checkout form if available
        const emailField = document.querySelector('input[name="checkout[email]"], input[type="email"]');
        if (emailField && emailField.value) {
          customerName = emailField.value.split('@')[0];
        }
      } catch (e) {
        // Use default if can't get name
      }

      // --- SECTION 1: DELIVERY SUMMARY ---
      const deliverySummary = [];

      if (this.selectedMethod) {
        const methodLabel = this.selectedMethod === 'delivery' ? 'Local Delivery' : 'Store Pickup';
        deliverySummary.push(`📦 Method: ${methodLabel}`);
      }

      if (this.selectedSlot) {
        const dateLabel = this.selectedSlot.dateLabel || this.selectedSlot.date;
        const timeslotLabel = this.selectedSlot.timeslotLabel || this.selectedSlot.timeslot;
        deliverySummary.push(`📅 Date: ${dateLabel}`);
        deliverySummary.push(`⏰ Time: ${timeslotLabel}`);
      }

      if (this.selectedMethod === 'delivery' && this.lastValidatedZipcode) {
        deliverySummary.push(`📍 ZIP: ${this.lastValidatedZipcode}`);
      }

      // --- SECTION 2: CARD MESSAGE ---
      let cardMessage = '';
      const cardMessageField = document.getElementById('greeting-message');
      if (cardMessageField && cardMessageField.value.trim()) {
        cardMessage = cardMessageField.value.trim();
        console.log('✅ Card message captured:', cardMessage.substring(0, 50) + '...');
      } else {
        cardMessage = `❌ Not provided by ${customerName}`;
        console.log('⚠️ Card message not provided');
      }

      // --- SECTION 3: SPECIAL INSTRUCTIONS ---
      let specialInstructions = '';
      const specialInstructionsField = document.getElementById('special-instructions');
      if (specialInstructionsField && specialInstructionsField.value.trim()) {
        specialInstructions = specialInstructionsField.value.trim();
        console.log('✅ Special instructions captured:', specialInstructions.substring(0, 50) + '...');
      } else {
        specialInstructions = `❌ Not provided by ${customerName}`;
        console.log('⚠️ Special instructions not provided');
      }

      // --- BUILD COMPLETE NOTE ---
      const noteSections = [];

      // Section 1: Delivery Summary
      noteSections.push('--- DELIVERY SUMMARY ---');
      noteSections.push(deliverySummary.join('\n'));

      // Section 2: Card Message
      noteSections.push('\n--- CARD MESSAGE ---');
      noteSections.push(cardMessage);

      // Section 3: Special Instructions
      noteSections.push('\n--- SPECIAL INSTRUCTIONS ---');
      noteSections.push(specialInstructions);

      // Section 4: Delivery Instructions (will be added by Shopify at checkout)
      noteSections.push('\n--- DELIVERY INSTRUCTIONS ---');
      noteSections.push('(To be filled at checkout - gate codes, parking, etc.)');

      const completeNote = noteSections.join('\n');

      // Get existing note and preserve any customer-entered delivery instructions
      let existingNote = cart.note || '';
      let customerDeliveryInstructions = '';

      // Check if there's already customer text (not our formatted sections)
      if (existingNote && !existingNote.includes('--- DELIVERY SUMMARY ---')) {
        // Preserve any existing customer note that's not our format
        customerDeliveryInstructions = existingNote.trim();
      }

      // Remove any existing auto-generated sections to prevent duplicates
      let cleanNote = existingNote;
      if (existingNote.includes('--- DELIVERY SUMMARY ---') || existingNote.includes('--- DELIVERY DETAILS ---')) {
        // Remove all auto-generated sections
        cleanNote = existingNote
          .replace(/--- DELIVERY SUMMARY ---[\s\S]*?(?=--- DELIVERY INSTRUCTIONS ---|$)/g, '')
          .replace(/--- DELIVERY DETAILS ---[\s\S]*?(?=\n\n---|$)/g, '')
          .replace(/--- CARD MESSAGE ---[\s\S]*?(?=\n\n---|$)/g, '')
          .replace(/--- SPECIAL INSTRUCTIONS ---[\s\S]*?(?=\n\n---|$)/g, '')
          .replace(/--- DELIVERY INSTRUCTIONS ---[\s\S]*?(?=\n\n---|$)/g, '')
          .trim();
      }

      // Build final note: preserved customer text + our structured sections
      let finalNote = completeNote;
      if (cleanNote && cleanNote !== customerDeliveryInstructions) {
        // If there's other content, prepend it
        finalNote = cleanNote + '\n\n' + completeNote;
      }

      // Build attributes payload from all hidden inputs anywhere in the DOM.
      // FIX (2026-05-08): Persist cart attributes server-side via /cart/update.js so they
      // survive any checkout path (regular Checkout, Shop Pay, PayPal, etc.) — not just the
      // cart form submission, which doesn't include the hidden inputs since they live outside <form id="cart">.
      const attrPayload = {};
      document.querySelectorAll('input[name^="attributes["]').forEach((input) => {
        const m = input.name.match(/^attributes\[([^\]]+)\]$/);
        if (m && input.value !== '') {
          attrPayload[m[1]] = input.value;
        }
      });

      // Update cart note + attributes in a single round-trip
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          note: finalNote,
          attributes: attrPayload
        })
      });

      console.log('✅ Cart note + attributes saved to server');
      console.log('📄 Note preview:', finalNote.substring(0, 200) + '...');
      console.log('🏷  Attributes:', attrPayload);

    } catch (error) {
      console.error('❌ Failed to update cart note:', error);
      console.error('Error details:', error.message);
      // Don't block checkout if note update fails
    }
  }

  async validateAndCheckout() {
    console.log('🔍 Guest checkout validation started');

    // First validate all requirements
    if (!this.validateCheckout()) {
      console.log('❌ Validation failed for guest checkout');
      const errors = this.getValidationErrors();
      this.showValidationBanner('Please complete your delivery details', errors);
      return false;
    }

    // Check session staleness
    if (!this.checkSessionStaleness()) {
      console.log('⚠️ Session staleness check failed');
      return false;
    }

    console.log('✅ All validations passed, proceeding to guest checkout');

    // Update cart note with delivery details before checkout
    await this.updateCartNoteWithDeliveryInfo();

    // Get form to collect attributes
    const form = document.getElementById('cart');
    if (!form) {
      console.error('❌ Cart form not found!');
      alert('Cart form not found. Please refresh the page.');
      return false;
    }

    // Use the same redirect logic as the main checkout button
    try {
      const formData = new FormData(form);

      // Build checkout URL with attributes as query parameters
      const params = new URLSearchParams();

      for (let [key, value] of formData.entries()) {
        if (key.startsWith('attributes[') && value) {
          console.log(`  📝 ${key}: ${value}`);
          params.append(key, value);
        }
      }

      // Smart pre-fill for ALL users (guest + logged-in) based on delivery method
      const isLoggedIn = document.body.classList.contains('customer-logged-in');

      if (this.selectedMethod === 'pickup') {
        // PICKUP: Pre-fill store ZIP (33169) for ALL users to ensure pickup location shows
        // This fixes the geolocation issue where distant/international customers don't see pickup option
        params.append('checkout[shipping_address][zip]', (window.FL_CONFIG && window.FL_CONFIG.storeZip) || '33169');
        params.append('checkout[shipping_address][city]', 'Miami');
        params.append('checkout[shipping_address][province]', 'FL');
        params.append('checkout[shipping_address][country]', 'US');
        console.log('📦 Store Pickup - pre-filling store ZIP: 33169 (fixes geolocation for all customers)');
      } else if (!isLoggedIn && this.selectedMethod === 'delivery' && this.lastValidatedZipcode) {
        // DELIVERY: Guest user - pre-fill validated ZIP, state, country
        params.append('checkout[shipping_address][zip]', this.lastValidatedZipcode);
        params.append('checkout[shipping_address][province]', 'FL');
        params.append('checkout[shipping_address][country]', 'US');
        console.log('👤 Guest user delivery - pre-filling ZIP:', this.lastValidatedZipcode);
      } else if (isLoggedIn) {
        console.log('🔐 Logged-in user - using saved address');
      }

      // Build the checkout URL
      let checkoutUrl = '/checkout';
      const paramString = params.toString();
      if (paramString) {
        checkoutUrl = `/checkout?${paramString}`;
      }

      console.log('🔄 Guest checkout redirecting to:', checkoutUrl);
      console.log('📦 Cart attributes will be included in checkout');

      // Redirect to checkout
      window.location.href = checkoutUrl;

    } catch (error) {
      console.error('❌ Exception during guest checkout redirect:', error);
      console.error('Error stack:', error.stack);
      alert('An error occurred. Please check the console and try again.');
      return false;
    }

    return true;
  }

  validateAndSignIn() {
    console.log('🔍 Sign in & Checkout validation started');

    // Check if validation passes
    const isValid = this.validateCheckout();

    if (!isValid) {
      // Show validation errors
      console.log('❌ Validation failed - showing errors');
      const errors = this.getValidationErrors();
      this.showValidationBanner('Please complete your delivery details', errors);
      return false;
    }

    // Validation passed - store checkout intent flag for post-login redirect
    console.log('✅ Validation passed - storing checkout intent and redirecting to login');
    sessionStorage.setItem('floralina_checkout_intent', 'true');
    sessionStorage.setItem('floralina_just_logged_in', 'true');

    const loginUrl = `/account/login?return_url=${encodeURIComponent('/cart')}`;
    window.location.href = loginUrl;
    return true;
  }

  /**
   * Show elegant validation banner instead of ugly alert()
   * @param {string} title - Banner title
   * @param {array} messages - Array of error messages to display as list items
   */
  showValidationBanner(title, messages) {
    if (!this.validationBanner) {
      console.warn('Validation banner not found, falling back to alert');
      alert(title + '\n\n' + messages.join('\n'));
      return;
    }

    // Set title
    if (this.validationBannerTitle) {
      this.validationBannerTitle.textContent = title;
    }

    // Build list of messages
    if (this.validationBannerList && messages && messages.length > 0) {
      this.validationBannerList.innerHTML = '';
      messages.forEach(msg => {
        const li = document.createElement('li');
        li.textContent = msg;
        this.validationBannerList.appendChild(li);
      });
    }

    // Show banner with animation
    this.validationBanner.style.display = 'block';
    // Force reflow for animation
    this.validationBanner.offsetHeight;
    this.validationBanner.classList.add('show');

    // Auto-hide after 8 seconds
    setTimeout(() => {
      this.hideValidationBanner();
    }, 8000);

    console.log('📢 Validation banner shown:', title);
  }

  /**
   * Hide validation banner
   */
  hideValidationBanner() {
    if (!this.validationBanner) return;

    this.validationBanner.classList.remove('show');

    // Wait for animation to complete before hiding
    setTimeout(() => {
      this.validationBanner.style.display = 'none';
    }, 300);

    console.log('📢 Validation banner hidden');
  }

  /**
   * Get validation error messages for banner
   * @returns {array} Array of validation error messages
   */
  getValidationErrors() {
    const errors = [];

    // Check delivery method selection
    if (!this.methodDelivery?.checked && !this.methodPickup?.checked) {
      errors.push('Select delivery method (Local Delivery or Store Pickup)');
    }

    // Check date/time slot
    if (!this.selectedSlot) {
      errors.push('Choose a delivery date and time slot');
    }

    // Check minimum order for BOTH delivery and pickup
    const cartTotal = this.config.cartTotal;
    const minAmount = this.config.minOrderAmount;

    if (cartTotal < minAmount) {
      errors.push(`Add $${(minAmount - cartTotal).toFixed(2)} more to reach the $${minAmount.toFixed(2)} minimum order`);
    }

    // Check delivery-specific requirements (ZIP code - LOCAL DELIVERY ONLY)
    if (this.selectedMethod === 'delivery') {
      const zipcode = this.zipcodeInput?.value.trim();
      if (!zipcode || zipcode.length === 0) {
        errors.push('Enter your ZIP code for delivery');
      } else if (zipcode.length < 5) {
        errors.push('Complete your 5-digit ZIP code');
      } else if (!this.lastValidatedZipcode) {
        errors.push('Verify your ZIP code is within our delivery area');
      }
    }

    return errors;
  }

  /**
   * Setup sticky sign-in/checkout section
   * Collapses after scrolling past delivery options
   */
  setupStickySignIn() {
    this.stickySignIn = document.getElementById('cart-ctas-sticky');
    if (!this.stickySignIn) {
      console.log('⚠️ Sticky sign-in element not found');
      return;
    }

    this.deliveryOptionsSection = document.querySelector('.cart-delivery-options');
    if (!this.deliveryOptionsSection) {
      console.log('⚠️ Delivery options section not found');
      return;
    }

    // Track scroll position and state
    let lastScrollY = window.scrollY;
    let isSticky = false;
    let isCollapsed = false;
    const stickyOffset = this.stickySignIn.offsetTop;
    const deliveryOptionsBottom = this.deliveryOptionsSection.offsetTop + this.deliveryOptionsSection.offsetHeight;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollingDown = currentScrollY > lastScrollY;

      // Activate sticky when scrolled past original position
      if (currentScrollY > stickyOffset && !isSticky) {
        isSticky = true;
        this.stickySignIn.classList.add('is-sticky');
        this.stickySignIn.style.setProperty('--sticky-height', `${this.stickySignIn.offsetHeight}px`);
        console.log('📌 Sign-in section is now sticky');
      } else if (currentScrollY <= stickyOffset && isSticky) {
        isSticky = false;
        isCollapsed = false;
        this.stickySignIn.classList.remove('is-sticky', 'is-collapsed');
        console.log('📍 Sign-in section returned to normal position');
      }

      // Collapse when scrolled past delivery options
      if (isSticky) {
        if (scrollingDown && currentScrollY > deliveryOptionsBottom && !isCollapsed) {
          isCollapsed = true;
          this.stickySignIn.classList.add('is-collapsed');
          console.log('🔽 Sign-in section collapsed');
        } else if (!scrollingDown && currentScrollY <= deliveryOptionsBottom && isCollapsed) {
          isCollapsed = false;
          this.stickySignIn.classList.remove('is-collapsed');
          console.log('🔼 Sign-in section expanded');
        }
      }

      lastScrollY = currentScrollY;
    };

    // Throttle scroll handler for better performance
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });

    console.log('📌 Sticky sign-in section initialized');
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.deliveryManager = new DeliveryManager();
});